import axios from 'axios';
import { normalizeObjectStrings } from '@/lib/utils';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API_ROOT = `${BACKEND_URL}/api`;
const CSRF_COOKIE_NAME = 'csrf_token';
const CSRF_HEADER_NAME = 'x-csrf-token';
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);
let securityConfigured = false;

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function shouldNormalizePayload(value) {
  return typeof value === 'string' || Array.isArray(value) || isPlainObject(value);
}

function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) {
    return '';
  }

  const prefix = `${encodeURIComponent(name)}=`;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  if (!match) {
    return '';
  }

  return decodeURIComponent(match.slice(prefix.length));
}

async function refreshCsrfCookie() {
  try {
    await axios.get(`${API_ROOT}/`, {
      withCredentials: true,
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
  } catch (error) {
    return '';
  }

  return readCookie(CSRF_COOKIE_NAME);
}

export function configureHttpSecurity() {
  if (securityConfigured) {
    return;
  }

  securityConfigured = true;
  axios.defaults.withCredentials = true;

  axios.interceptors.request.use((config) => {
    const nextConfig = { ...config };
    const method = String(nextConfig.method || 'get').toLowerCase();

    if (typeof nextConfig.withCredentials === 'undefined') {
      nextConfig.withCredentials = true;
    }

    if (!UNSAFE_METHODS.has(method)) {
      return nextConfig;
    }

    nextConfig.headers = {
      ...(nextConfig.headers || {}),
      'X-Requested-With': 'XMLHttpRequest'
    };

    const csrfToken = readCookie(CSRF_COOKIE_NAME);
    if (csrfToken) {
      nextConfig.headers[CSRF_HEADER_NAME] = csrfToken;
    }

    return nextConfig;
  });

  axios.interceptors.response.use(
    (response) => {
      if (shouldNormalizePayload(response?.data)) {
        response.data = normalizeObjectStrings(response.data);
      }
      return response;
    },
    async (error) => {
      if (error?.response && shouldNormalizePayload(error.response.data)) {
        error.response.data = normalizeObjectStrings(error.response.data);
      }

      const originalRequest = error?.config;
      const detail = error?.response?.data?.detail;
      const method = String(originalRequest?.method || 'get').toLowerCase();
      const isCsrfFailure =
        error?.response?.status === 403 &&
        typeof detail === 'string' &&
        detail.toLowerCase().includes('sessao protegida');

      if (!originalRequest || originalRequest.__csrfRetried || !UNSAFE_METHODS.has(method) || !isCsrfFailure) {
        return Promise.reject(error);
      }

      originalRequest.__csrfRetried = true;
      const nextToken = await refreshCsrfCookie();

      if (!nextToken) {
        return Promise.reject(error);
      }

      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        [CSRF_HEADER_NAME]: nextToken,
        'X-Requested-With': 'XMLHttpRequest'
      };

      return axios(originalRequest);
    }
  );
}
